import CheckContainer from 'components/CheckContainer';
import styled from '@emotion/styled';
import { Divider } from 'antd';

import EnterpriseUnlimitedTag from './EnterpriseUnlimitedTag';
import PricingLink from './PricingLink';
import RecomendTag from './RecomendTag';
import { PanelTitle, List, ListItem, PackTip } from './Common';
import { isQuickChat } from 'productLine';
import { T, useTranslate } from '@tolgee/react';
import { catalogPackageLabel } from '../catalogAdapter';

export interface PackageCheckboxProps {
  value?: Pay.PackageId;
  recommendPackageId?: Pay.PackageId;
  isEnterprise?: boolean;
  list: Pay.PackageInfoV2['packageData'];
  disabled?: (isEnterprise: boolean, value?: Pay.PackageV2) => boolean;
  onChange?: (isEnterprise: boolean, value?: Pay.PackageV2) => void;
}

/** 所有行按最高卡片等高，企业版换行后也保持一致。 */
const PlanList = styled(List)({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 278px), 278px))',
  gridAutoRows: '1fr',
  alignItems: 'stretch',
  '&.compact': {
    gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 220px), 220px))',
  },
});

const PlanListItem = styled(ListItem)({
  display: 'flex',
  height: 'auto',
  minWidth: 0,
});

export const PackageCheckboxGroup = (props: PackageCheckboxProps) => {
  const { value, list, recommendPackageId, isEnterprise = false, disabled, onChange } = props;
  const { t } = useTranslate();

  // 企业版套餐
  const enterprise = (
    <PlanListItem>
      <CheckItem
        checked={isEnterprise}
        onClick={() => {
          onChange?.(true);
        }}
      >
        <PackageCntWrap>
          <span>{t('market.企业版')}</span>
          <span className="py-1">
            {t('scene.managementCenter.sellerPlan.subscriptionTab.enterprisePlan.description')}
          </span>
          <div>
            <EnterpriseUnlimitedTag />
          </div>
        </PackageCntWrap>
      </CheckItem>
    </PlanListItem>
  );

  return (
    <PlanList className={isQuickChat ? 'compact' : ''}>
      {list.map((item) => {
        const { accountNum, id, aiServiceBuyerNum = 0, aiOrderMonthlyQuota = 0 } = item;
        const checked = id === value;
        const isDisabled = disabled?.(false, item);

        return (
          <PlanListItem key={id}>
            <CheckItem
              disabled={isDisabled}
              checked={checked}
              onChange={() => {
                if (checked) {
                  return;
                }
                onChange?.(false, item);
              }}
            >
              <PackageCntWrap>
                <span>{catalogPackageLabel(item, t)}</span>
                <span>
                  <T
                    keyName="scene.managementCenter.sellerPlan.subscriptionTab.agentSeats.label"
                    params={{
                      com: <b />,
                      count: accountNum,
                    }}
                  />
                  <span className="px-0.5">+</span>
                  <T
                    keyName="scene.managementCenter.sellerPlan.subscriptionTab.adminSeat.label"
                    params={{
                      com: <b />,
                      count: 1,
                    }}
                  />
                </span>
                {!isQuickChat && (
                  <div className="flex flex-col items-start gap-1">
                    {/* AI精准回复订单数额度（词条待 tolgee 补录，defaultValue 兜底） */}
                    <AiServiceTimes>
                      <T
                        keyName="scene.managementCenter.sellerPlan.subscriptionTab.aiOrderPerMonth.label"
                        defaultValue="AI即时精准回复 {count} 单/月"
                        params={{ count: aiOrderMonthlyQuota }}
                      />
                    </AiServiceTimes>
                    <AiServiceTimes>
                      {t(
                        'scene.managementCenter.sellerPlan.subscriptionTab.aiFollowUpPerMonth.label',
                        {
                          count: aiServiceBuyerNum,
                        },
                      )}
                    </AiServiceTimes>
                  </div>
                )}
              </PackageCntWrap>
            </CheckItem>
            {recommendPackageId === id && <RecomendTag />}
          </PlanListItem>
        );
      })}
      {!isQuickChat && enterprise}
    </PlanList>
  );
};

export const PackageSelectPanel = (props: PackageCheckboxProps) => {
  const { t } = useTranslate();

  return (
    <div>
      <PanelTitle>
        {t('scene.managementCenter.sellerPlan.subscriptionTab.label.selectPlan')}
        <Divider type="vertical" />
        {!isQuickChat && <PackTip>{t('以下套餐含有1个管理员账号')}</PackTip>}
      </PanelTitle>
      <PackageCheckboxGroup {...props} />
      {!isQuickChat && <PricingLink />}
    </div>
  );
};

const PackageCntWrap = styled.div`
  min-width: 0;
  width: 100%;
  min-height: 80px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  overflow-wrap: anywhere;

  b {
    font-size: 20px;
    padding: 0 4px;
  }
`;
const AiServiceTimes = styled.span`
  /* min-height 而非 height：多语言长文案换行时盒子随内容撑开，避免溢出被下一行标签盖住 */
  min-height: 18px;
  padding: 2px 4px;
  font-weight: 400;
  font-size: 12px;
  color: var(--fett-scenario-accent-text);
  line-height: 18px;
  background: var(--fett-bg-info-tip);
  border-radius: 4px;
`;

const CheckItem = styled(CheckContainer)({
  display: 'flex',
  alignItems: 'flex-start',
  flex: 1,
  minWidth: 0,
  width: '100%',
  borderRadius: '4px',
  padding: 16,
});
